import { useLayoutEffect, useRef, useState } from "react";
import { Input } from "antd";
import {
  CheckOutlined,
  CommentOutlined,
  TeamOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import {
  useActiveCategories,
  useEnabledItems,
  useMenuAddons,
  useMenuModifiers,
  useMenuStations,
} from "../../../../hooks/useMenu";
import { useViewport } from "../../../../hooks/useViewport";
import {
  buildGroupColorLookup,
  stationColorOf,
  type ColorBy,
} from "./catalogColors";
import { KbdChip } from "../modalPrimitives";
import {
  buildOrderRows,
  calculateOrderTotal,
  calculateOrderPricing,
} from "./orderSummaryUtils";
import { formatSignedBillPercent } from "../billDiscount";
import { KitchenHoldAllPicker } from "./KitchenHoldAll";
import {
  holdTimeCountsOf,
  holdTimeOf,
  type KitchenHoldTime,
} from "./kitchenHoldItems";
import { OrderPanelRow } from "./OrderPanelRow";
import type { KitchenHoldControls } from "./useKitchenHoldChoice";

const { TextArea } = Input;

interface OrderItem {
  itemId: number;
  quantity: number;
  modifiers: number[];
  addons: Record<number, Record<number, number>>; // addonId -> { itemId -> количество }
  comment?: string;
  /** Своето време за кухнята, ако е различно от това за всички (kitchenHoldItems.ts). */
  kitchenHoldTime?: KitchenHoldTime;
}

interface OrderPanelContentProps {
  orderItems: OrderItem[];
  comment: string;
  showCommentInput: boolean;
  onCommentChange: (comment: string) => void;
  onToggleComment: () => void;
  onQuantityChange: (index: number, delta: number) => void;
  /** Отваря модала за редакция на модификатори/коментар от молива на реда
   *  (EditModifiersModal, рендира се в Ordering); при обобщен ред получава
   *  всички индекси на групата. */
  onItemEdit: (indices: number[], orderItem: OrderItem) => void;
  onSubmit: () => void;
  /** true, докато createBill заявката тече — блокира „Поръчай" */
  submitting: boolean;
  /** "panel" (десктоп панел) или "sheet" (мобилен bottom sheet) — сменя само фона на редовете и празния текст */
  variant?: "panel" | "sheet";
  /** Клиент с отстъпка/надбавка по сметката (знаков процент, >0 = отстъпка) —
   *  блок над „Общо" с основата и корекцията; null = без корекция. */
  billDiscount?: { percent: number; clientName: string | null } | null;
  /** По какво се цвети лентата отляво на реда (настройка на ресторанта colorBy):
   *  станция → една ивица на станция (продукт с 2 станции = 2 половини);
   *  група → цветът на групата. */
  colorBy?: ColorBy;
  /** Времето за кухнята на всеки ред и „Едно време за всички редове"
   *  (useKitchenHoldChoice в Ordering); без него — редовете са без време. */
  kitchenHold?: KitchenHoldControls;
}

/**
 * Споделено тяло на „Преглед на поръчката" — ползва се и от десктоп панела
 * (OrderPanel), и от мобилния bottom sheet (OrderSheet). Съдържа редовете на
 * текущата поръчка със степери, коментара към поръчката и бутоните
 * „Коментар" / „Поръчай · {сума}".
 */
export const OrderPanelContent = ({
  orderItems,
  comment,
  showCommentInput,
  onCommentChange,
  onToggleComment,
  onQuantityChange,
  onItemEdit,
  onSubmit,
  submitting,
  variant = "panel",
  billDiscount = null,
  colorBy = "group",
  kitchenHold,
}: OrderPanelContentProps) => {
  const { t } = useTranslation();
  const allItems = useEnabledItems();
  const allAddons = useMenuAddons();
  const allModifiers = useMenuModifiers();
  const stations = useMenuStations();
  const categories = useActiveCategories();
  const groupColor = buildGroupColorLookup(categories);
  // Цветовете на лентата отляво на реда — по станции или по група
  const stripeColors = (item: {
    stationIds: number[];
    categoryId: number | null;
  }) => {
    if (colorBy === "none") return [];
    if (colorBy === "station") {
      return item.stationIds
        .map((id) => stationColorOf(stations.find((s) => s.id === id)))
        .filter((color): color is string => !!color);
    }
    const color = groupColor(item.categoryId);
    return color ? [color] : [];
  };

  // „Обобщено" — по подразбиране изключено; слива еднаквите редове в един със
  // сборно количество. Живее тук, за да важи и за панела, и за sheet-а.
  const [summarized, setSummarized] = useState(false);

  const total = calculateOrderTotal(orderItems, allItems, allAddons);
  // Клиентски процент по сметката: „Общо" и „Поръчай" показват коригираната
  // сума (BE ще я приложи при поръчката), блокът отгоре — основата и разликата
  const billPercent = billDiscount?.percent ?? 0;
  const pricing = calculateOrderPricing(
    orderItems,
    allItems,
    allAddons,
    billPercent,
  );
  const payableTotal = billPercent !== 0 ? pricing.finalTotal : total;
  const isEmpty = orderItems.length === 0;
  const isSheet = variant === "sheet";
  const rows = buildOrderRows(orderItems, summarized);
  // Времето за кухнята на редовете — без интернет го няма (касата не задържа).
  // Обобщеният ред е с едно време: ключът за сливане го включва.
  const holdEnabled = !!kitchenHold && !kitchenHold.lockedTooltip;
  const rowHoldTime = (row: (typeof rows)[number]) =>
    holdTimeOf(row.orderItem, kitchenHold?.defaultTime ?? "now");
  // Под „Поръчай": колко реда с какво време („1 сега · 3 ръчно"); при всичко
  // „Сега" — нищо.
  const holdSummary = holdEnabled
    ? holdTimeCountsOf(rows.map(rowHoldTime))
        .map(({ time, count }) =>
          time === "now"
            ? t("ordering.kitchenHold.summaryNow", { count })
            : time === "manual"
              ? t("ordering.kitchenHold.summaryManual", { count })
              : t("ordering.kitchenHold.summaryMinutes", { count, minutes: time }),
        )
        .join(" · ")
    : "";
  // В панела под 1440px редът с бройката, времето и степера е тесен (на
  // 1024px остават ~228px) — времето е само надпис, без иконка и стрелка.
  const { isWide } = useViewport();
  const compactHoldChip = !isSheet && !isWide;

  // При всяко МАРКИРАНЕ редът с последно добавеното се докарва в полезрението:
  // при дълга поръчка сервитьорът вижда какво току-що е маркирал, не началото
  // на списъка. Маркираното винаги стои в ПОСЛЕДНИЯ запис на кошницата
  // (Ordering.tsx добавя нов запис или слива само с последния), затова
  // сигналът е точно това: кошницата се е удължила или последният запис е
  // пораснал. Степерът „+" на ред по средата, редакция от молива, намаляване
  // и махане НЕ скролират — ръката е на реда, който се пипа. В обобщен
  // изглед целевият ред може да е и по средата — затова `nearest`, не дъното.
  const listRef = useRef<HTMLDivElement>(null);
  const recordCount = orderItems.length;
  const lastQuantity = orderItems[recordCount - 1]?.quantity ?? 0;
  const previousRef = useRef({ recordCount: 0, lastQuantity: 0 });
  useLayoutEffect(() => {
    const previous = previousRef.current;
    previousRef.current = { recordCount, lastQuantity };
    const marked =
      recordCount > previous.recordCount ||
      (recordCount === previous.recordCount &&
        lastQuantity > previous.lastQuantity);
    if (!marked) return;
    listRef.current
      ?.querySelector("[data-order-last]")
      ?.scrollIntoView({ block: "nearest" });
  }, [recordCount, lastQuantity]);

  return (
    <div className="flex flex-col flex-1 min-h-0">
      {/* „Едно време за всички редове" — от хапчето в заглавието (KitchenHoldAll) */}
      {holdEnabled && kitchenHold?.allPickerOpen && (
        <KitchenHoldAllPicker controls={kitchenHold} />
      )}

      {/* Списък с редовете на поръчката */}
      {/* Скролбарът е скрит (както в каталога) — без вертикална линия */}
      <div
        ref={listRef}
        className="flex-1 min-h-0 overflow-y-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden p-3 flex flex-col gap-1"
      >
        {isEmpty ? (
          <div className="flex-1 grid place-items-center px-2.5 py-10 text-center text-sm text-pos-muted">
            {isSheet ? t("ordering.emptyOrderShort") : t("ordering.emptyOrder")}
          </div>
        ) : (
          rows.map((row, index) => {
            const item = allItems.find((i) => i.id === row.orderItem.itemId);
            if (!item) return null;
            const rowIndex = row.indices[0];
            return (
              <OrderPanelRow
                key={index}
                row={row}
                item={item}
                allItems={allItems}
                allAddons={allAddons}
                allModifiers={allModifiers}
                stripes={stripeColors(item)}
                isSheet={isSheet}
                isLast={row.indices.includes(orderItems.length - 1)}
                onItemEdit={onItemEdit}
                onQuantityChange={onQuantityChange}
                kitchenHold={
                  holdEnabled && kitchenHold
                    ? {
                        time: rowHoldTime(row),
                        pickerOpen: kitchenHold.openRowIndex === rowIndex,
                        onTogglePicker: () => kitchenHold.onToggleRowPicker(rowIndex),
                        onPick: (time) => kitchenHold.onPickRow(row.indices, time),
                        onApplyToAll: kitchenHold.onPickAll,
                        compact: compactHoldChip,
                      }
                    : null
                }
              />
            );
          })
        )}
      </div>

      {/* Футър: коментар към поръчката + Общо + бутони */}
      <div className="border-t border-pos-line2 px-4 py-3.5 flex flex-col gap-[11px]">
        {showCommentInput && (
          <div>
            <div className="font-pos-mono text-[11px] font-semibold uppercase tracking-[.06em] text-pos-dim mb-1.5">
              {t("ordering.orderComment")}
            </div>
            <TextArea
              value={comment}
              onChange={(e) => onCommentChange(e.target.value)}
              placeholder={t("ordering.commentPlaceholder")}
              // Enter тригерва „Поръчай" (shortcut ⏎) вместо нов ред;
              // Shift+Enter остава за нов ред.
              onPressEnter={(e) => {
                if (e.shiftKey) return;
                e.preventDefault();
                onSubmit();
              }}
              rows={2}
              autoFocus
              className="!bg-pos-panel !border-pos-ctl !rounded-xl !text-[13px]"
            />
          </div>
        )}

        {/* Клиент с отстъпка/надбавка по сметката: кой е избран + ±X%,
            отдолу основата и колко се добавя/маха. Показва се и при празна
            кошница — цените в каталога вече са коригирани. Клиент с 0% също
            се показва (закачен е към сметката). */}
        {billDiscount && (
          <div className="rounded-xl bg-pos-disc-bg px-3 py-2 flex flex-col gap-1">
            <div className="flex items-center justify-between gap-2">
              <span
                className={`min-w-0 flex items-center gap-1.5 font-semibold text-pos-ink ${isSheet ? "text-[12.5px]" : "text-[14.5px] leading-[19px]"}`}
              >
                <TeamOutlined className="shrink-0 text-[13px] text-pos-disc-ink" />
                <span className="min-w-0 truncate">
                  {billDiscount.clientName ?? t("ordering.billDiscountClient")}
                </span>
              </span>
              <span
                className={`shrink-0 font-pos-mono font-bold text-pos-disc-ink ${isSheet ? "text-[13px]" : "text-[15px] leading-[19.5px]"}`}
              >
                {formatSignedBillPercent(billDiscount.percent)}
              </span>
            </div>
            <div
              className={`flex items-center justify-between gap-2 font-pos-mono text-pos-soft ${isSheet ? "text-[11.5px]" : "text-[13.5px] leading-[17px]"}`}
            >
              <span>
                {t("ordering.billDiscountBase")} {pricing.baseTotal.toFixed(2)}
              </span>
              <span className="font-semibold text-pos-disc-ink">
                {t(
                  billDiscount.percent < 0
                    ? "ordering.billSurchargeLabel"
                    : "ordering.billDiscountLabel",
                )}{" "}
                {pricing.adjustment >= 0 ? "+" : "−"}
                {Math.abs(pricing.adjustment).toFixed(2)}
              </span>
            </div>
          </div>
        )}

        {/* Ред „Общо": вляво чекбоксът „Обобщено" (крие се при празна
            поръчка), вдясно етикетът и сумата. Без рамка и фон — само
            чекбокс + текст, оранжеви при включване. */}
        <div className="flex items-center justify-between gap-2">
          {!isEmpty ? (
            <button
              type="button"
              onClick={() => setSummarized((current) => !current)}
              aria-pressed={summarized}
              className={`inline-flex items-center gap-2 border-0 bg-transparent p-0 text-[12.5px] font-semibold cursor-pointer transition-colors ${
                summarized ? "text-pos-accent" : "text-pos-ink2"
              }`}
            >
              <span
                className={`grid h-4 w-4 flex-shrink-0 place-items-center rounded-[5px] border ${
                  summarized
                    ? "border-pos-accent bg-pos-accent text-white"
                    : "border-pos-check bg-pos-card"
                }`}
              >
                {summarized && <CheckOutlined className="text-[9px]" />}
              </span>
              {t("ordering.summarized")}
            </button>
          ) : (
            <span className="text-sm text-pos-soft2">{t("common.total")}</span>
          )}
          <span className="flex items-baseline gap-2 min-w-0">
            {!isEmpty && (
              <span className="text-sm text-pos-soft2">
                {t("common.total")}
              </span>
            )}
            <span className="font-pos-mono text-[22px] font-bold text-pos-ink whitespace-nowrap">
              {payableTotal.toFixed(2)} EUR
            </span>
          </span>
        </div>

        <div className="flex gap-2.5">
          <button
            type="button"
            onClick={onToggleComment}
            className={`shrink-0 h-11 rounded-xl border px-4 text-sm font-semibold inline-flex items-center gap-2 cursor-pointer ${
              showCommentInput
                ? "bg-pos-marked border-pos-accent text-pos-accent"
                : "bg-pos-card border-pos-ctl text-pos-ink2"
            }`}
          >
            <CommentOutlined className="text-pos-dim text-[15px]" />
            {t("common.comment")}
          </button>
          <button
            type="button"
            onClick={onSubmit}
            disabled={isEmpty || submitting}
            className="relative flex-1 min-w-0 h-11 rounded-xl bg-pos-accent hover:bg-pos-accent-ink text-white text-[15px] font-bold whitespace-nowrap cursor-pointer disabled:opacity-45 disabled:cursor-not-allowed transition-colors flex flex-col items-center justify-center"
          >
            <span className="leading-[18px]">
              {t("ordering.order")}
              {/* Сумата в бутона: в sheet-а (телефон) винаги; в десктоп панела едва
                  от xl (≥1280px) — под това панелът (27% от ширината) е твърде тесен и
                  етикетът се пречупваше на два реда. */}
              <span className={isSheet ? "" : "hidden xl:inline"}>
                {" · "}
                {payableTotal.toFixed(2)}
              </span>
            </span>
            {/* Какво ще стане с редовете („1 сега · 3 ръчно") — само при
                задържане; дълъг надпис се реже (min-w-0 на бутона) */}
            {holdSummary && (
              <span className="max-w-full truncate px-2 text-[11px] font-semibold leading-[14px] text-white/85">
                {holdSummary}
              </span>
            )}
            {/* Kbd chip за Enter — само на десктоп панела (на телефон няма
                клавиатура). */}
            {!isSheet && <KbdChip shortcut="⏎" colorClass="text-white/80" />}
          </button>
        </div>
      </div>
    </div>
  );
};
